import "server-only"

import type { Metadata } from "next"
import { cacheLife } from "next/cache"
import { cache } from "react"
import { env } from "@/env"
import { wordPressApi } from "@/services/api"
import {
  DATA_FRESHNESS_MS,
  toNextRevalidateSeconds,
} from "@/utilities/data-freshness"

/**
 * SEO helper for Next.js App Router.
 *
 * - Pulls site defaults from WordPress (Triibo theme settings)
 * - Allows per-route overrides
 * - Produces Next.js `Metadata` (OG/Twitter/robots/canonical/icons)
 */

type IndexingMode = "production-only" | "always" | "never"

interface ThemeSettingsResponse {
  general?: {
    title?: unknown
    description?: unknown
    favicon?: unknown
  }
}

const asTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined
  }

  const trimmed = value.trim()

  return trimmed.length > 0 ? trimmed : undefined
}

export interface RouteSeoOverride {
  /** Page-specific title (without site name suffix). */
  title?: string
  description?: string
  /**
   * Absolute canonical URL or a pathname starting with "/".
   * If omitted, canonical will be derived from `path`.
   */
  canonical?: string
  /** Whether search engines should index this route (subject to `indexingMode`). */
  index?: boolean
  /** Whether search engines should follow links on this route. */
  follow?: boolean
  /** Open Graph/Twitter image URL or pathname. */
  ogImage?: string
  /** ISO date string or `YYYY-MM-DD` for sitemap-ish consumers. */
  lastModified?: string
  /** 0.0 - 1.0. */
  priority?: number
}

export interface ResolvedSeo {
  path: string
  title: string
  description: string
  canonicalUrl: string
  siteUrl: string
  siteName: string
  faviconUrl?: string
  ogImageUrl: string
  robots: {
    index: boolean
    follow: boolean
  }
  lastModified?: string
  priority?: number
}

export interface SeoConfig {
  siteUrl: string
  siteName: string
  authorName: string
  twitterCreator?: string
  indexingMode: IndexingMode
  defaultOgImagePath: string
  favicon?: string
  routes: Record<string, RouteSeoOverride>
}

const DEFAULT_SITE_NAME = "Marketplace Triibo"
const DEFAULT_AUTHOR = "Triibo"
const DEFAULT_DESCRIPTION =
  "Marketplace Triibo: produtos, ofertas e novidades em um só lugar."
const DEFAULT_OG_IMAGE_PATH = "/images/preview.jpeg"

const getIndexingMode = (): IndexingMode => {
  // Good SEO default: prevent staging/dev from being indexed unless you opt in.
  const appEnv = env.NEXT_PUBLIC_ENV
  if (appEnv === "production") return "production-only"
  return "never"
}

const getSiteUrl = (): string => {
  return env.NEXT_PUBLIC_SITE_URL ?? "http://localhost:3000"
}

const normalizePath = (path: string): string => {
  if (!path) return "/"
  const withoutQuery = path.split("?")[0] ?? "/"
  return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`
}

const stripHtml = (value: string): string => {
  // Basic sanitation for WP/woo fields that can include HTML.
  return value
    .replaceAll(/<[^>]*>/g, " ")
    .replaceAll(/\s+/g, " ")
    .trim()
}

const toAbsoluteUrl = (siteUrl: URL, urlOrPath: string): URL => {
  if (urlOrPath.startsWith("http://") || urlOrPath.startsWith("https://")) {
    return new URL(urlOrPath)
  }
  const path = urlOrPath.startsWith("/") ? urlOrPath : `/${urlOrPath}`
  return new URL(path, siteUrl)
}

const buildTitle = (pageTitle: string, siteName: string): string => {
  const trimmed = pageTitle.trim()
  if (!trimmed) return siteName
  if (trimmed.toLowerCase() === siteName.trim().toLowerCase()) return siteName
  return `${trimmed} | ${siteName}`
}

const getThemeSettings = cache(
  async (): Promise<ThemeSettingsResponse | null> => {
    "use cache"
    // Use shared freshness policy: DATA_FRESHNESS_MS.seoDefaults (1 hour)
    cacheLife({
      revalidate: toNextRevalidateSeconds(DATA_FRESHNESS_MS.seoDefaults),
    })

    try {
      const { data } = await wordPressApi.get<ThemeSettingsResponse>(
        "/triibo/v1/settings/theme"
      )

      return data
    } catch (error: unknown) {
      // SEO should degrade gracefully if WP is down.
      console.error("Failed to fetch theme settings for SEO:", error)
      return null
    }
  }
)

export const getSeoConfig = cache(
  async (overrides: Partial<SeoConfig> = {}): Promise<SeoConfig> => {
    "use cache"
    // Use shared freshness policy: DATA_FRESHNESS_MS.seoDefaults (1 hour)
    cacheLife({
      revalidate: toNextRevalidateSeconds(DATA_FRESHNESS_MS.seoDefaults),
    })

    const themeSettings = await getThemeSettings()
    const siteUrl = overrides.siteUrl ?? getSiteUrl()
    const themeTitle = asTrimmedString(themeSettings?.general?.title)
    const siteName = overrides.siteName ?? themeTitle ?? DEFAULT_SITE_NAME

    const themeDescription = asTrimmedString(
      themeSettings?.general?.description
    )
    const favicon = asTrimmedString(themeSettings?.general?.favicon)

    return {
      siteUrl,
      siteName,
      authorName: overrides.authorName ?? DEFAULT_AUTHOR,
      twitterCreator: overrides.twitterCreator,
      indexingMode: overrides.indexingMode ?? getIndexingMode(),
      defaultOgImagePath: overrides.defaultOgImagePath ?? DEFAULT_OG_IMAGE_PATH,
      favicon: overrides.favicon ?? favicon,
      routes: {
        "/": {
          title: siteName,
          description: themeDescription || DEFAULT_DESCRIPTION,
          canonical: siteUrl,
          index: true,
          follow: true,
          ogImage: overrides.defaultOgImagePath ?? DEFAULT_OG_IMAGE_PATH,
          lastModified: "2024-01-01",
          priority: 0.2,
        },
        ...(overrides.routes ?? {}),
      },
    }
  }
)

const getRobotsIndexFollow = (
  indexingMode: IndexingMode,
  route: RouteSeoOverride | undefined
): { index: boolean; follow: boolean } => {
  const routeIndex = route?.index ?? true
  const routeFollow = route?.follow ?? true

  switch (indexingMode) {
    case "always":
      return { index: routeIndex, follow: routeFollow }
    case "never":
      return { index: false, follow: false }
    default:
      return env.NEXT_PUBLIC_ENV === "production"
        ? { index: routeIndex, follow: routeFollow }
        : { index: false, follow: false }
  }
}

export const getSeoForPath = cache(
  async (
    path: string,
    options: {
      configOverrides?: Partial<SeoConfig>
      routeOverrides?: RouteSeoOverride
    } = {}
  ): Promise<ResolvedSeo> => {
    "use cache"
    // Use shared freshness policy: DATA_FRESHNESS_MS.seoDefaults (1 hour)
    cacheLife({
      revalidate: toNextRevalidateSeconds(DATA_FRESHNESS_MS.seoDefaults),
    })

    const normalizedPath = normalizePath(path)
    const config = await getSeoConfig(options.configOverrides)

    const siteUrl = new URL(config.siteUrl)
    const routeFromMap = config.routes[normalizedPath]
    const route = {
      ...(routeFromMap ?? {}),
      ...(options.routeOverrides ?? {}),
    }

    const canonicalUrl = toAbsoluteUrl(
      siteUrl,
      route.canonical ?? normalizedPath
    )

    const robots = getRobotsIndexFollow(config.indexingMode, route)

    const title = buildTitle(route.title ?? config.siteName, config.siteName)

    const description = stripHtml(
      route.description ??
        config.routes["/"]?.description ??
        DEFAULT_DESCRIPTION
    )

    const ogImageUrl = toAbsoluteUrl(
      siteUrl,
      route.ogImage ?? config.defaultOgImagePath
    )

    // Theme favicon can be absolute or relative; fall back to none.
    const faviconUrl = config.favicon
      ? toAbsoluteUrl(siteUrl, config.favicon).toString()
      : undefined

    return {
      path: normalizedPath,
      title,
      description,
      canonicalUrl: canonicalUrl.toString(),
      siteUrl: siteUrl.toString(),
      siteName: config.siteName,
      faviconUrl,
      ogImageUrl: ogImageUrl.toString(),
      robots,
      lastModified: route.lastModified,
      priority: route.priority,
    }
  }
)

export const generateMetadataForPath = async (
  path: string,
  options?: Parameters<typeof getSeoForPath>[1]
): Promise<Metadata> => {
  const seo = await getSeoForPath(path, options)
  const config = await getSeoConfig(options?.configOverrides)

  const canonical = seo.canonicalUrl
  const siteUrl = seo.siteUrl
  const ogImage = seo.ogImageUrl
  const favicon = seo.faviconUrl

  const metadata: Metadata = {
    title: seo.title,
    description: seo.description,
    generator: config.authorName,
    applicationName: seo.siteName,
    referrer: "origin-when-cross-origin",
    authors: [
      {
        name: config.authorName,
        url: siteUrl,
      },
    ],
    creator: seo.siteName,
    publisher: seo.siteName,
    alternates: {
      canonical,
    },
    robots: {
      index: seo.robots.index,
      follow: seo.robots.follow,
      googleBot: {
        index: seo.robots.index,
        follow: seo.robots.follow,
        "max-image-preview": "large",
        "max-snippet": -1,
        "max-video-preview": -1,
      },
    },
    openGraph: {
      title: seo.title,
      description: seo.description,
      url: canonical,
      siteName: seo.siteName,
      type: "website",
      images: [
        {
          url: ogImage,
          width: 1200,
          height: 630,
          alt: seo.siteName,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title: seo.title,
      description: seo.description,
      creator: config.twitterCreator,
      images: [ogImage],
    },
  }

  if (favicon) {
    metadata.icons = {
      icon: favicon,
    }
  }

  return metadata
}

export const getIndexedRoutes = cache(
  async (
    configOverrides?: Partial<SeoConfig>
  ): Promise<
    Pick<ResolvedSeo, "path" | "canonicalUrl" | "lastModified" | "priority">[]
  > => {
    "use cache"
    // Use shared freshness policy: DATA_FRESHNESS_MS.seoDefaults (1 hour)
    cacheLife({
      revalidate: toNextRevalidateSeconds(DATA_FRESHNESS_MS.seoDefaults),
    })

    const config = await getSeoConfig(configOverrides)
    const siteUrl = new URL(config.siteUrl)

    const routes = Object.entries(config.routes)
      .filter(([, route]) => route.index !== false)
      .map(([path, route]) => ({
        path,
        canonicalUrl: toAbsoluteUrl(
          siteUrl,
          route.canonical ?? path
        ).toString(),
        lastModified: route.lastModified,
        priority: route.priority,
      }))

    return routes
  }
)
